import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ORDERS_REQUEST } from "./types";

interface OrdersState {
  projects: any | null;
  adjustmentTypes: any | null;
  allOrders: any[] | null;
  deliveryQCOrders: any[] | null;
  orderDetails: any | null;
  orderIssues: any | null;
  issueDetails: any | null;
  invoices: any[] | null;
  invoiceOrders: any[] | null;
  resultsDelivered: any | null;
  orderInspection: any | null;
  assignableInspectionFirms: any | null;
  orderHistory: any | null;
  orderInspectionForm: any | null;
  searchResults: any | null;
  orderIssuesCount: any | null;
  createIssueSearchResults: any | null;
  qcModification: any | null;
  orderAttachments: any[] | null;
  loading: boolean;
  searchLoading: boolean;
  error: any | null;
}

const ordersInitialState: OrdersState = {
  projects: null,
  adjustmentTypes: null,
  allOrders: null,
  deliveryQCOrders: null,
  orderDetails: null,
  orderIssues: null,
  issueDetails: null,
  invoices: null,
  invoiceOrders: null,
  resultsDelivered: null,
  orderInspection: null,
  assignableInspectionFirms: null,
  orderHistory: null,
  orderInspectionForm: null,
  searchResults: null,
  orderIssuesCount: null,
  createIssueSearchResults: null,
  qcModification: null,
  orderAttachments: null,
  loading: false,
  searchLoading: false,
  error: null,
};

export const ordersSlice = createSlice({
  name: ORDERS_REQUEST,
  initialState: ordersInitialState,
  reducers: {
    getProjectsRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getProjectsSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.projects = action.payload.response;
    },
    getProjectsFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getAdjustmentTypesRequest(state) {
      state.loading = true;
      state.error = null;
    },
    getAdjustmentTypesSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.adjustmentTypes = action.payload.response;
    },
    getAdjustmentTypesFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getAllOrdersRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getAllOrdersSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.allOrders = action.payload.response;
    },
    getAllOrdersFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
     getDeliveryQCOrdersRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getDeliveryQCOrdersSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.deliveryQCOrders = action.payload.response;
    },
    getDeliveryQCOrdersFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getOrderDetailsRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getOrderDetailsSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.orderDetails = action.payload.response;
    },
    getOrderDetailsFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    resetOrdersData(state) {
      state.projects = null;
      state.loading = false;
      state.error = null;
    },
    getOrderIssuesRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getOrderIssuesSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.orderIssues = action.payload.response;
    },
    getOrderIssuesFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getIssueDetailsRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getIssueDetailsSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.issueDetails = action.payload.response;
    },
    getIssueDetailsFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getInvoicesRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getInvoicesSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.invoices = action.payload.response;
    },
    getInvoicesFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getResultsDeliveredRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getResultsDeliveredSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.resultsDelivered = action.payload.response;
    },
    getResultsDeliveredFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getInvoiceOrdersRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getInvoiceOrdersSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.invoiceOrders = action.payload.response;
    },
    getInvoiceOrdersFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getOrderInspectionRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getOrderInspectionSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.orderInspection = action.payload.response;
    },
    getOrderInspectionFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getAssignableInspectionFirmsRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getAssignableInspectionFirmsSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.assignableInspectionFirms = action.payload.response;
    },
    getAssignableInspectionFirmsFailure(
      state,
      action: PayloadAction<{ error: any }>
    ) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getOrderHistoryRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getOrderHistorySuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.orderHistory = action.payload.response;
    },
    getOrderHistoryFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getOrderInspectionFormRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getOrderInspectionFormSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.orderInspectionForm = action.payload.response;
    },
    getOrderInspectionFormFailure(
      state,
      action: PayloadAction<{ error: any }>
    ) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getSearchOrdersRequest(state, action: PayloadAction<any>) {
      state.searchLoading = true;
      state.searchResults = null;
      state.error = null;
    },
    getSearchOrdersSuccess(state, action: PayloadAction<{ response: any }>) {
      state.searchLoading = false;
      state.searchResults = action.payload.response;
    },
    getSearchOrdersFailure(state, action: PayloadAction<{ error: any }>) {
      state.searchLoading = false;
      state.error = action.payload.error;
    },
    resetSearchResults(state) {
      state.searchResults = null;
      state.searchLoading = false;
    },
    getOrderIssuesCountRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getOrderIssuesCountSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.orderIssuesCount = action.payload.response;
    },
    getOrderIssuesCountFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getCreateIssueSearchOrderRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getCreateIssueSearchOrderSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.createIssueSearchResults = action.payload.response;
    },
    getCreateIssueSearchOrderFailure(
      state,
      action: PayloadAction<{ error: any }>
    ) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getQCModificationRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getQCModificationSuccess(state, action: PayloadAction<{ response: any }>) {
      state.loading = false;
      state.qcModification = action.payload.response;
    },
    getQCModificationFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
    getOrderAttachmentsRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.error = null;
    },
    getOrderAttachmentsSuccess(
      state,
      action: PayloadAction<{ response: any }>
    ) {
      state.loading = false;
      state.orderAttachments = action.payload.response;
    },
    getOrderAttachmentsFailure(state, action: PayloadAction<{ error: any }>) {
      state.loading = false;
      state.error = action.payload.error;
    },
  },
});

export const {
  getProjectsRequest,
  getProjectsSuccess,
  getProjectsFailure,
  getAdjustmentTypesRequest,
  getAdjustmentTypesSuccess,
  getAdjustmentTypesFailure,
  getAllOrdersRequest,
  getAllOrdersSuccess,
  getDeliveryQCOrdersFailure,
  getDeliveryQCOrdersRequest,
  getDeliveryQCOrdersSuccess,
  getAllOrdersFailure,
  getOrderDetailsRequest,
  getOrderDetailsSuccess,
  getOrderDetailsFailure,
  resetOrdersData,
  getOrderIssuesRequest,
  getOrderIssuesSuccess,
  getOrderIssuesFailure,
  getIssueDetailsRequest,
  getIssueDetailsSuccess,
  getIssueDetailsFailure,
  getInvoicesRequest,
  getInvoicesSuccess,
  getInvoicesFailure,
  getResultsDeliveredRequest,
  getResultsDeliveredSuccess,
  getResultsDeliveredFailure,
  getInvoiceOrdersRequest,
  getInvoiceOrdersSuccess,
  getInvoiceOrdersFailure,
  getOrderInspectionRequest,
  getOrderInspectionSuccess,
  getOrderInspectionFailure,
  getAssignableInspectionFirmsRequest,
  getAssignableInspectionFirmsSuccess,
  getAssignableInspectionFirmsFailure,
  getOrderHistoryRequest,
  getOrderHistorySuccess,
  getOrderHistoryFailure,
  getOrderInspectionFormRequest,
  getOrderInspectionFormSuccess,
  getOrderInspectionFormFailure,
  getSearchOrdersRequest,
  getSearchOrdersSuccess,
  getSearchOrdersFailure,
  resetSearchResults,
  getOrderIssuesCountRequest,
  getOrderIssuesCountSuccess,
  getOrderIssuesCountFailure,
  getCreateIssueSearchOrderRequest,
  getCreateIssueSearchOrderSuccess,
  getCreateIssueSearchOrderFailure,
  getQCModificationRequest,
  getQCModificationSuccess,
  getQCModificationFailure,
  getOrderAttachmentsRequest,
  getOrderAttachmentsSuccess,
  getOrderAttachmentsFailure,
} = ordersSlice.actions;

export default ordersSlice.reducer;
